import Dashboard from "../../../layout/Dashboard";
import { Content, Heading } from "../../../styles/OverviewStyle";
import { RFMain } from "../../../styles/RatesFeesStyle";
import { ListHead, ListRow } from "../../../styles/CrypoAssetStyle";
import { Icon } from '@iconify/react'
import { useEffect, useState } from 'react'
import { TabNavigator } from "../components/Navigator";
import { feeService } from '../../../services/FeeService';
import type { TransactionFee } from '../../../../types/ApiTypes';
import { FeeType, TransactionType } from '../../../../types/ApiTypes';
import { formatWithCommas } from '@/utils/numberHelpers';

const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
   [TransactionType.CRYPTO_PURCHASE]: 'Crypto Purchase',
   [TransactionType.CRYPTO_SALE]: 'Crypto Sale',
   [TransactionType.CRYPTO_DEPOSIT]: 'Crypto Deposit',
   [TransactionType.GIFTCARD_PURCHASE]: 'Giftcard Purchase',
   [TransactionType.GIFTCARD_SALE]: 'Giftcard Sale',
   [TransactionType.WITHDRAWAL]: 'Withdrawal',
   [TransactionType.TOPUP]: 'Topup',
   [TransactionType.VIRTUAL_ACCOUNT_TOPUP]: 'Virtual Account Topup',
   [TransactionType.CONVERSION]: 'Conversion',
}

type GroupedFees = Record<TransactionType, TransactionFee[]>

export default function FeesPage() {
   const [fees, setFees] = useState<TransactionFee[]>([])
   const [loading, setLoading] = useState(true)
   const [editingId, setEditingId] = useState<string | null>(null)
   const [editForm, setEditForm] = useState({
      feeType: FeeType.FIXED,
      feeValue: '',
      minAmount: '',
      maxAmount: ''
   })
   const [showAddForm, setShowAddForm] = useState(false)
   const [addForm, setAddForm] = useState({
      transactionType: TransactionType.CRYPTO_PURCHASE,
      feeType: FeeType.FIXED,
      feeValue: '',
      minAmount: '',
      maxAmount: ''
   })

   useEffect(() => {
      loadFees()
   }, [])

   const loadFees = async () => {
      try {
         const response = await feeService.getTransactionFees()
         if (response.status === 'success' && response.data) {
            setFees(response.data)
         }
      } catch (error) {
         console.error('Failed to load fees:', error)
      } finally {
         setLoading(false)
      }
   }

   const groupedFees: GroupedFees = fees.reduce((acc, fee) => {
      if (!acc[fee.transactionType]) {
         acc[fee.transactionType] = []
      }
      acc[fee.transactionType].push(fee)
      return acc
   }, {} as GroupedFees)

   const startEdit = (fee: TransactionFee) => {
      setEditingId(fee.id)
      setEditForm({
         feeType: fee.feeType,
         feeValue: fee.feeValue,
         minAmount: fee.minAmount,
         maxAmount: fee.maxAmount
      })
   }

   const cancelEdit = () => {
      setEditingId(null)
      setEditForm({ feeType: FeeType.FIXED, feeValue: '', minAmount: '', maxAmount: '' })
   }

   const saveEdit = async (id: string) => {
      try {
         const data: Partial<{ feeType: FeeType; feeValue: number; minAmount: number; maxAmount: number }> = {}
         if (editForm.feeType) data.feeType = editForm.feeType
         if (editForm.feeValue) data.feeValue = parseFloat(editForm.feeValue)
         if (editForm.minAmount) data.minAmount = parseFloat(editForm.minAmount)
         if (editForm.maxAmount) data.maxAmount = parseFloat(editForm.maxAmount)

         await feeService.updateTransactionFee(id, data)
         await loadFees()
         cancelEdit()
      } catch (error) {
         console.error('Failed to update fee:', error)
      }
   }

   const deleteFee = async (id: string) => {
      if (!confirm('Are you sure you want to delete this fee?')) return
      try {
         await feeService.deleteTransactionFee(id)
         await loadFees()
      } catch (error) {
         console.error('Failed to delete fee:', error)
      }
   }

   const handleAddFee = async () => {
      try {
         const data = {
            transactionType: addForm.transactionType,
            feeType: addForm.feeType,
            feeValue: parseFloat(addForm.feeValue),
            minAmount: parseFloat(addForm.minAmount),
            maxAmount: parseFloat(addForm.maxAmount)
         }
         await feeService.createTransactionFee(data)
         await loadFees()
         setShowAddForm(false)
         setAddForm({
            transactionType: TransactionType.CRYPTO_PURCHASE,
            feeType: FeeType.FIXED,
            feeValue: '',
            minAmount: '',
            maxAmount: ''
         })
      } catch (error) {
         console.error('Failed to create fee:', error)
      }
   }

   const getCurrencySymbol = (transactionType: TransactionType) =>
      transactionType === TransactionType.VIRTUAL_ACCOUNT_TOPUP ? '$' : '₦'

   const formatFeeValue = (feeType: FeeType, feeValue: string, transactionType: TransactionType) => {
      return feeType === FeeType.PERCENTAGE
         ? `${feeValue}%`
         : `${getCurrencySymbol(transactionType)}${formatWithCommas(feeValue)}`
   }

   return (
      <Dashboard title="Rates & Fees / Fees" icon="solar:tag-price-linear" active="rates_fees">
         <Content>
            <Heading>Rates & Fees</Heading>

            <TabNavigator active="fees" />

            <RFMain>
               <div>
                  <div className="flex justify-between items-center mb-4">
                     <h3 className="text-base font-medium text-[#101018]">Transaction Fees</h3>
                     <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="px-4 py-2 bg-[#AF7AD5] text-white text-sm rounded-md hover:bg-[#9B5FC4] transition-colors"
                     >
                        {showAddForm ? 'Cancel' : 'Add New Fee'}
                     </button>
                  </div>

                  {showAddForm && (
                     <div className="mb-6 p-4 border border-[#E7E7E9] rounded-lg bg-[#FAFAFA]">
                        <h4 className="text-sm font-medium text-[#101018] mb-3">Add New Fee</h4>
                        <div className="grid grid-cols-5 gap-3">
                           <select
                              value={addForm.transactionType}
                              onChange={e => setAddForm(s => ({ ...s, transactionType: e.target.value as TransactionType }))}
                              className="h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                           >
                              {Object.values(TransactionType).map(type => (
                                 <option key={type} value={type}>{TRANSACTION_TYPE_LABELS[type]}</option>
                              ))}
                           </select>
                           <input
                              placeholder="Min Amount"
                              value={addForm.minAmount}
                              onChange={e => setAddForm(s => ({ ...s, minAmount: e.target.value }))}
                              className="h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                           />
                           <input
                              placeholder="Max Amount"
                              value={addForm.maxAmount}
                              onChange={e => setAddForm(s => ({ ...s, maxAmount: e.target.value }))}
                              className="h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                           />
                           <select
                              value={addForm.feeType}
                              onChange={e => setAddForm(s => ({ ...s, feeType: e.target.value as FeeType }))}
                              className="h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                           >
                              <option value={FeeType.FIXED}>Fixed</option>
                              <option value={FeeType.PERCENTAGE}>Percentage</option>
                           </select>
                           <input
                              placeholder="Fee Value"
                              value={addForm.feeValue}
                              onChange={e => setAddForm(s => ({ ...s, feeValue: e.target.value }))}
                              className="h-9 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                           />
                        </div>
                        <button
                           onClick={handleAddFee}
                           className="mt-3 px-4 py-2 bg-[#AF7AD5] text-white text-sm rounded-md hover:bg-[#9B5FC4] transition-colors"
                        >
                           Create Fee
                        </button>
                     </div>
                  )}

                  {loading ? (
                     <div className="flex justify-center py-8">
                        <Icon icon="svg-spinners:ring-resize" width={32} className="text-[#AF7AD5]" />
                     </div>
                  ) : (
                     Object.entries(groupedFees).map(([transactionType, typeFees]) => (
                        <div key={transactionType} className="mb-6">
                           <h4 className="text-sm font-medium text-[#101018] mb-2 px-4">
                              {TRANSACTION_TYPE_LABELS[transactionType as TransactionType]}
                              {transactionType === TransactionType.VIRTUAL_ACCOUNT_TOPUP && (
                                 <span className="ml-2 text-xs font-normal text-[#6D6D72]">(amounts in USD)</span>
                              )}
                           </h4>
                           <ListHead style={{ gridTemplateColumns: '200px 150px 150px 150px 150px 120px', columnGap: '20px' }}>
                              <div>Range</div>
                              <div>Min Amount</div>
                              <div>Max Amount</div>
                              <div>Fee Type</div>
                              <div>Fee Value</div>
                              <div className="text-right pr-2">Actions</div>
                           </ListHead>

                           {typeFees.map(fee => {
                              const isEditing = editingId === fee.id
                              return (
                                 <ListRow key={fee.id} style={{ gridTemplateColumns: '200px 150px 150px 150px 150px 120px', columnGap: '20px' }}>
                                    <div className="text-sm text-[#6D6D72]">
                                       {getCurrencySymbol(fee.transactionType)}{formatWithCommas(fee.minAmount)} - {getCurrencySymbol(fee.transactionType)}{formatWithCommas(fee.maxAmount)}
                                    </div>

                                    <div>
                                       {isEditing ? (
                                          <input
                                             value={editForm.minAmount}
                                             onChange={e => setEditForm(s => ({ ...s, minAmount: e.target.value }))}
                                             className="w-full h-8 px-2 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                          />
                                       ) : (
                                          <span className="text-sm text-[#6D6D72]">{getCurrencySymbol(fee.transactionType)}{formatWithCommas(fee.minAmount)}</span>
                                       )}
                                    </div>

                                    <div>
                                       {isEditing ? (
                                          <input
                                             value={editForm.maxAmount}
                                             onChange={e => setEditForm(s => ({ ...s, maxAmount: e.target.value }))}
                                             className="w-full h-8 px-2 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                          />
                                       ) : (
                                          <span className="text-sm text-[#6D6D72]">{getCurrencySymbol(fee.transactionType)}{formatWithCommas(fee.maxAmount)}</span>
                                       )}
                                    </div>

                                    <div>
                                       {isEditing ? (
                                          <select
                                             value={editForm.feeType}
                                             onChange={e => setEditForm(s => ({ ...s, feeType: e.target.value as FeeType }))}
                                             className="w-full h-8 px-2 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                          >
                                             <option value={FeeType.FIXED}>Fixed</option>
                                             <option value={FeeType.PERCENTAGE}>Percentage</option>
                                          </select>
                                       ) : (
                                          <span className="text-sm text-[#6D6D72] capitalize">{fee.feeType}</span>
                                       )}
                                    </div>

                                    <div>
                                       {isEditing ? (
                                          <input
                                             value={editForm.feeValue}
                                             onChange={e => setEditForm(s => ({ ...s, feeValue: e.target.value }))}
                                             className="w-full h-8 px-2 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                          />
                                       ) : (
                                          <span className="text-sm text-[#6D6D72]">{formatFeeValue(fee.feeType, fee.feeValue, fee.transactionType)}</span>
                                       )}
                                    </div>

                                    <div className="flex items-center justify-end gap-2">
                                       {isEditing ? (
                                          <>
                                             <button aria-label="Cancel" onClick={cancelEdit}>
                                                <Icon icon="mdi:close" width={16} className="text-[#DD524D]" />
                                             </button>
                                             <button aria-label="Save" onClick={() => saveEdit(fee.id)}>
                                                <Icon icon="mdi:check" width={16} className="text-[#4CAF50]" />
                                             </button>
                                          </>
                                       ) : (
                                          <>
                                             <button
                                                className="text-[#B3B3B6] text-xs hover:underline cursor-pointer inline-flex items-center gap-1"
                                                onClick={() => startEdit(fee)}
                                                style={{
                                                   padding: '6px 9px',
                                                   borderRadius: '6px',
                                                   border: '1px solid #EDEDEE',
                                                   boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.04)'
                                                }}
                                             >
                                                <Icon icon="solar:pen-2-linear" width={14} />
                                                Edit
                                             </button>
                                             <button
                                                className="text-[#DD524D] text-xs hover:underline cursor-pointer inline-flex items-center gap-1"
                                                onClick={() => deleteFee(fee.id)}
                                                style={{
                                                   padding: '6px 9px',
                                                   borderRadius: '6px',
                                                   border: '1px solid #EDEDEE',
                                                   boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.04)'
                                                }}
                                             >
                                                <Icon icon="solar:trash-bin-minimalistic-linear" width={14} />
                                                Delete
                                             </button>
                                          </>
                                       )}
                                    </div>
                                 </ListRow>
                              )
                           })}
                        </div>
                     ))
                  )}

                  {!loading && fees.length === 0 && (
                     <div className="text-center py-12 text-[#6D6D72]">
                        No transaction fees configured yet. Click "Add New Fee" to create one.
                     </div>
                  )}
               </div>
            </RFMain>
         </Content>
      </Dashboard>
   )
}
